import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { sendAnswers } from "../../../services/api";
import {
  clearCollectedAnswers,
  clearCompletedSections,
  getCollectedAnswers,
  getCompletedSections,
  setCurrentSection,
} from "../../../utils/trainingStorage";

interface DashboardUser {
  sub?: string;
  email?: string;
}

interface UseDashboardProgressParams {
  selectedProduct: string;
  sectionsCount: number;
  user?: DashboardUser;
}

export const useDashboardProgress = ({
  selectedProduct,
  sectionsCount,
  user,
}: UseDashboardProgressParams) => {
  const navigate = useNavigate();
  const [enabledSections, setEnabledSections] = useState<number>(1);
  const [allSectionsCompleted, setAllSectionsCompleted] = useState<boolean>(false);

  useEffect(() => {
    const completedSections = getCompletedSections(selectedProduct);
    if (completedSections) {
      setEnabledSections(completedSections + 1);
    } else {
      setEnabledSections(1);
    }
  }, [selectedProduct]);

  const handleSectionClick = (sectionIndex: number, type: string, nextRoute: string) => {
    if (sectionIndex <= enabledSections && !type) {
      setCurrentSection(selectedProduct, sectionIndex);
      navigate(`/${selectedProduct}/section${sectionIndex}`);
    } else if (type === "practical-task") {
      setCurrentSection(selectedProduct, sectionIndex);
      navigate(`/${selectedProduct}${nextRoute}`);
    }
  };

  const handleRestartClick = () => {
    clearCompletedSections(selectedProduct);
    clearCollectedAnswers(selectedProduct);
    setEnabledSections(1);
    setAllSectionsCompleted(false);
    navigate(`/${selectedProduct}/dashboard`);
  };

  useEffect(() => {
    if (enabledSections > sectionsCount) {
      setAllSectionsCompleted(true);
      const collectedAnswers = getCollectedAnswers(selectedProduct);
      if (collectedAnswers.length > 0) {
        sendAnswers({
          user: { id: user?.sub || "", email: user?.email || "" },
          answers: collectedAnswers,
          selectedProduct,
        }).then(() => {
          clearCollectedAnswers(selectedProduct);
        });
      }
    }
  }, [enabledSections, sectionsCount, selectedProduct, user?.email, user?.sub]);

  return {
    allSectionsCompleted,
    enabledSections,
    handleRestartClick,
    handleSectionClick,
  };
};
